import { formatDuration } from './formatDuration';

const PLAN_CARD_STYLES = [
  {
    theme: 'blue',
    cardClass:
      'border border-sky-100/80 bg-gradient-to-b from-[#CECEFA] via-[#CECEFA]/30 to-white',
    buttonClass: 'border-[#0040BF] text-[#0040BF] bg-white',
    hoverCardClass: 'group-hover:shadow-[0_16px_40px_rgba(26,135,204,0.22)]',
    hoverPriceClass: 'group-hover:text-[#0040BF]',
    hoverDurationClass: 'group-hover:text-[#0040BF]',
    hoverButtonClass:
      'group-hover:enabled:border-transparent group-hover:enabled:bg-gradient-to-r group-hover:enabled:from-[#1A87CC] group-hover:enabled:to-[#0040BF] group-hover:enabled:text-white group-hover:enabled:shadow-md',
    dividerClass: 'border-sky-200',
    highlighted: false,
  },
  {
    theme: 'purple',
    cardClass:
      'border border-violet-100/80 bg-gradient-to-b from-[#EDCFFF] via-[#EDCFFF]/30 to-white',
    buttonClass: 'border-[#9369CC] text-[#9369CC] bg-white',
    hoverCardClass: 'group-hover:shadow-[0_16px_40px_rgba(147,105,204,0.28)]',
    hoverPriceClass: 'group-hover:text-[#9369CC]',
    hoverDurationClass: 'group-hover:text-[#9369CC]',
    hoverButtonClass:
      'group-hover:enabled:border-transparent group-hover:enabled:bg-gradient-to-r group-hover:enabled:from-[#9369CC] group-hover:enabled:to-[#6A74E1] group-hover:enabled:text-white group-hover:enabled:shadow-md',
    dividerClass: 'border-violet-200',
    highlighted: false,
  },
  {
    theme: 'cyan',
    cardClass:
      'border border-emerald-100/80 bg-gradient-to-b from-[#D4F7FF] via-[#D4F7FF]/30 to-white',
    buttonClass: 'border-[#00C3D0] text-[#00C0E8] bg-white',
    hoverCardClass: 'group-hover:shadow-[0_16px_40px_rgba(0,192,232,0.22)]',
    hoverPriceClass: 'group-hover:text-[#00C0E8]',
    hoverDurationClass: 'group-hover:text-[#00C0E8]',
    hoverButtonClass:
      'group-hover:enabled:border-transparent group-hover:enabled:bg-gradient-to-r group-hover:enabled:from-[#00C3D0] group-hover:enabled:to-[#1A87CC] group-hover:enabled:text-white group-hover:enabled:shadow-md',
    dividerClass: 'border-cyan-200',
    highlighted: false,
  },
];

const DEFAULT_PLAN_FEATURES = ['Unlimited Courses', 'Chapter wise Quiz', 'Live Doubt Support'];

const SUBJECT_GRADIENTS = {
  Mathematics: 'from-amber-500 via-orange-600 to-red-800',
  Biology: 'from-emerald-600 via-teal-600 to-cyan-800',
  Chemistry: 'from-lime-600 via-green-600 to-emerald-800',
  CHEMISTRY: 'from-lime-600 via-green-600 to-emerald-800',
  Physics: 'from-violet-700 via-purple-700 to-indigo-900',
};

const SUBJECT_PILL_CLASS = {
  Mathematics: 'bg-orange-500 text-white',
  Biology: 'bg-blue-500 text-white',
  Chemistry: 'bg-green-600 text-white',
  Physics: 'bg-violet-600 text-white',
};

const PLACEHOLDER_AVATAR =
  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=face';

const PLACEHOLDER_PERSON =
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=500&fit=crop&crop=face';

export function parsePlanFeatures(html) {
  if (!html) return [];
  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.innerHTML = html;
    const items = Array.from(div.querySelectorAll('li'))
      .map((li) => li.textContent?.trim())
      .filter(Boolean);
    if (items.length) return items;
    const text = div.textContent?.trim();
    return text ? [text.slice(0, 80)] : [];
  }
  return DEFAULT_PLAN_FEATURES;
}

export function stripHtmlText(html) {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent?.trim() || '';
  }
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function formatPlanDuration(plan) {
  if (plan.end_date) return 'Until Exam';
  if (plan.duration_days === 30) return '1 Month';
  if (plan.duration_days === 90) return '3 Month';
  if (plan.duration_days) return `${plan.duration_days} Days`;
  return 'Flexible';
}

function mapPlan(plan, styleIndex) {
  const style = PLAN_CARD_STYLES[styleIndex % PLAN_CARD_STYLES.length];
  const hasDiscount =
    Boolean(plan.has_discount) &&
    plan.discount_price != null &&
    Number(plan.discount_price) > 0;
  const price = hasDiscount ? plan.discount_price : plan.price;

  return {
    id: plan.id,
    name: plan.title,
    price: Math.round(price),
    originalPrice: Math.round(plan.price),
    priceFormatted: hasDiscount ? plan.discount_price_formatted : plan.price_formatted,
    originalPriceFormatted: plan.price_formatted,
    hasDiscount,
    discountPercentage: plan.discount_percentage,
    duration: formatPlanDuration(plan),
    features: parsePlanFeatures(plan.description).length
      ? parsePlanFeatures(plan.description)
      : DEFAULT_PLAN_FEATURES,
    categoryId: String(plan.category_id),
    categoryName: plan.category_name,
    badge: plan.tag || null,
    ...style,
  };
}

export function groupPlansByCategory(plans = []) {
  const groups = {};

  plans.forEach((plan) => {
    const key = String(plan.category_id);
    if (!groups[key]) {
      groups[key] = {
        id: key,
        label: plan.category_name?.toUpperCase().includes('CLASS')
          ? plan.category_name.replace('Class ', 'CLASS ').toUpperCase()
          : `CLASS ${plan.category_name}`,
        plans: [],
      };
    }
    groups[key].plans.push(mapPlan(plan, groups[key].plans.length));
  });

  return groups;
}

const EXPERT_STYLES = [
  { bgClass: 'bg-blue-600', subject: 'Biology', subjectColorClass: 'group-hover:text-blue-400' },
  { bgClass: 'bg-zinc-600', subject: 'Mathematics', subjectColorClass: 'group-hover:text-orange-400' },
  { bgClass: 'bg-orange-500', subject: 'Physics', subjectColorClass: 'group-hover:text-violet-400' },
  { bgClass: 'bg-yellow-400', subject: 'Chemistry', subjectColorClass: 'group-hover:text-lime-400' },
];

const SUBJECT_EXPERT_COLORS = {
  Biology: 'group-hover:text-blue-400',
  Mathematics: 'group-hover:text-orange-400',
  Physics: 'group-hover:text-violet-400',
  Chemistry: 'group-hover:text-lime-400',
};

export function mapInstructor(instructor, index) {
  const style = EXPERT_STYLES[index % EXPERT_STYLES.length];
  const imageUrl = instructor.profile_image_url?.trim();
  const titleSubject = (instructor.title || '')
    .replace(/\s*Expert\s*$/i, '')
    .trim();
  const subject = titleSubject || style.subject;
  const subjectColorClass = SUBJECT_EXPERT_COLORS[subject] || style.subjectColorClass;

  return {
    id: instructor.id,
    name: instructor.name,
    image: imageUrl || PLACEHOLDER_AVATAR,
    subject,
    subjectColorClass,
    bgClass: style.bgClass,
  };
}

function normalizeSubjectLabel(subject) {
  if (!subject) return 'Course';
  const trimmed = String(subject).trim();
  if (!trimmed) return 'Course';

  return trimmed
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function mapCourse(course) {
  if (!course) return null;

  const subject = normalizeSubjectLabel(course.subject_name || course.subject);
  const gradient = SUBJECT_GRADIENTS[subject] || SUBJECT_GRADIENTS[course.subject] || 'from-violet-700 via-purple-700 to-indigo-900';
  const rating = course.rating ?? course.total_rating ?? 0;
  const chapters = course.chapters_count ?? course.total_chapters ?? 0;
  const lessons = course.lessons_count ?? course.total_lessons ?? 0;
  const classLabel = course.category_name || course.class || 'Class 12';
  const instructorName = course.instructor_name || course.instructor?.name || 'Instructor';
  const instructorImage =
    course.instructor_profile_image_url || course.instructor?.profile_image_url || PLACEHOLDER_AVATAR;
  const subjectColorCode = course.subject_color_code || null;
  const subjectId = course.subject_id || course.subject?.id || null;
  const categoryId = course.category_id || course.category?.id || null;
  const description =
    stripHtmlText(course.subtitle) ||
    stripHtmlText(course.description)?.slice(0, 160) ||
    '';

  return {
    id: course.id,
    title: (course.title || '').trim(),
    description,
    thumbnailTitle: course.title,
    thumbnailSubtitle: subject,
    thumbnailLabel: subject || course.title?.split(' ')?.[0] || 'Course',
    thumbnailClass: classLabel,
    thumbnailUrl: course.thumbnail_url,
    classLabel,
    categoryId,
    subject,
    subjectId,
    subjectClass: SUBJECT_PILL_CLASS[subject] || 'bg-gray-600 text-white',
    subjectColorCode,
    tagClass: subjectColorCode ? 'text-white' : SUBJECT_PILL_CLASS[subject] || 'bg-gray-600 text-white',
    classBadgeClass: 'border-2 border-[#9369CC] bg-[#9369CC33] text-[#9369CC]',
    gradient,
    rating,
    chapters,
    lessons,
    lectures: lessons,
    duration: course.duration_minutes ? formatDuration(course.duration_minutes) : '—',
    courseRating: rating,
    instructorName,
    instructorImage,
    instructorId: course.instructor_id || course.instructor?.id || null,
    mostChosen: Boolean(course.popular || course.is_new_lunches),
    newLaunchLabel: course.new_lunches_text || (course.popular ? 'Most Chosen' : null),
    isFavorite: Boolean(course.is_favorite),
    isSubscriptionTaken: Boolean(course.is_subscription_taken),
    lessonsCompleted: course.lessons_completed ?? 0,
    subcategoryId: course.subcategory_id,
  };
}

export function mapTestimonial(testimonial) {
  const image = testimonial.profile_photo_url || null;
  return {
    id: testimonial.id,
    name: testimonial.student_name,
    role: testimonial.class_name,
    quote: testimonial.message,
    image,
    thumb: image,
    rating: testimonial.rating,
  };
}

export function mapShortVideo(video) {
  const title = video.title?.trim() || 'Concept reel';
  const subject = video.subject?.trim() || '';

  return {
    id: video.id,
    title,
    subject,
    image: video.thumbnail_url || null,
    videoUrl: video.video_url || video.youtube_url || null,
    alt: title || subject || 'Concept snapshot',
  };
}

export function mapBanner(banner) {
  return {
    id: banner.id,
    title: banner.title,
    subtitle: banner.subtitle,
    imageUrl: banner.image_url,
    type: banner.banner_type,
  };
}

export function mapDashboardData(apiData) {
  if (!apiData) return null;

  return {
    plansByCategory: groupPlansByCategory(apiData.plans),
    instructors: (apiData.instructors || []).map((instructor, index) =>
      mapInstructor(instructor, index)
    ),
    shortVideos: (apiData.short_videos || []).map(mapShortVideo),
    banners: (apiData.banners || []).map(mapBanner),
    testimonials: (apiData.testimonials || []).map(mapTestimonial),
    freeCourses: (apiData.free_courses || []).map(mapCourse),
    newLaunchesCourses: (apiData.new_launches_courses || []).map(mapCourse),
  };
}
